/**
 * Окно «Сравнить» на карточке изображения: входы генерации двумя колонками — «Товар» (фото платья) и «Референс»
 * (сцена, лицо, вставки; №440), затем «Генерация» и «Доработка» (стрелки ‹ › и ← → листают доработки). Поверх генерации и доработки
 * картинок — дизлайк («Брак», левый верх) и лайк (метка этапа: «В улучшение» / «В публикацию» / «Утверждено», правый верх); повтор снимает отметку.
 * Esc закрывает; в полях ввода клавиши окну не отдаём.
 * `host` (№459, каталог): окно в чужой странице — отметки идут через неё (`onPick`), а не прямым `POST …/pick`
 * (лайк там не метит кадр целиком, а открывает рамку 3:4), картинки открывают карточку в новой вкладке.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, Heart, HeartOff, Send, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { ErrorLine } from "@/components/trends/ui";
import { mediaUrl, thumbUrl } from "@/lib/media";
import type { GenInput } from "@/lib/project";
import { stepIndex } from "@/lib/resultCompare";
import { loadResultCard, pickResult, resultKeys, resultPath, RESULTS_PREFIX, type ResultCard } from "@/lib/results";
import { likeState, splitInputs, startIndices, triptych, type Shot } from "@/lib/triptych";
import { enhanceNote, STAGE_HINT, STAGE_LABEL, stageOf } from "@/lib/verdict";

function Arrow({ dir, disabled, onClick }: { dir: "prev" | "next"; disabled: boolean; onClick: () => void }) {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      className="grid size-9 shrink-0 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30"
      disabled={disabled}
      onClick={onClick}
      title={dir === "prev" ? "Предыдущая (←)" : "Следующая (→)"}
      aria-label={dir === "prev" ? "Предыдущая доработка" : "Следующая доработка"}
    >
      <Icon size={18} />
    </button>
  );
}

/** Хозяин окна (№459): сам решает, что значат «Брак» и лайк; окно только показывает и зовёт. */
export type CompareHost = {
  onPick: (status: "picked" | "rejected") => void;
  busy: boolean;
  /** Ошибка отметки хозяина (его запрос, не `pick`) — показывается в окне. */
  error: string | null;
  /** Лайк доступен (в каталоге — только платью из каталога). */
  canLike: boolean;
  /** Подсказка лайка вместо подсказки этапа. */
  likeHint: string;
};

const STAGE_ICON = { enhance: Sparkles, publish: Send, approve: Heart } as const;

function ShotColumn({
  title,
  shot,
  busy,
  onPick,
  arrows,
  hideMark,
  markHint,
  newTab,
}: {
  title: string;
  shot: Shot;
  busy: boolean;
  onPick: (variantId: number, status: "picked" | "rejected") => void;
  arrows?: { index: number; count: number; onIndex: (i: number) => void };
  /** Вид картинки ещё неизвестен (карточка исходника не пришла) — кнопку этапа не показываем. */
  hideMark?: boolean;
  /** Подсказка лайка вместо `STAGE_HINT` (№459). */
  markHint?: string | undefined;
  /** Картинка открывает карточку в новой вкладке (№459: окно каталога не должно терять страницу). */
  newTab?: boolean;
}) {
  const { liked, disliked } = likeState(shot.pick);
  const stage = stageOf(shot.kind);
  const StageIcon = STAGE_ICON[stage];
  const heart = "absolute top-2 grid size-8 place-items-center rounded-full bg-black/30 disabled:opacity-60";
  return (
    <figure className="m-0 grid min-w-0 content-start justify-items-center gap-2" data-annot={title}>
      <div className="flex w-full min-w-0 items-center justify-center gap-2">
        {arrows ? <Arrow dir="prev" disabled={arrows.index <= 0} onClick={() => arrows.onIndex(arrows.index - 1)} /> : null}
        <div className="relative inline-block max-w-full">
          <Link to={resultPath(shot.variantId)} {...(newTab ? { target: "_blank" } : {})} title="Открыть карточку изображения">
            <img
              key={shot.variantId}
              src={mediaUrl(shot.path)}
              alt=""
              className="block h-auto max-h-[70vh] w-auto max-w-full rounded-[4px] lg:max-h-[calc(100vh-10rem)]"
            />
          </Link>
          <button
            type="button"
            className={clsx(heart, "left-2", disliked ? "text-err" : "text-white")}
            aria-pressed={disliked}
            title="Брак"
            aria-label="Брак"
            disabled={busy}
            onClick={() => onPick(shot.variantId, "rejected")}
          >
            <HeartOff size={20} className={disliked ? "fill-current" : undefined} />
          </button>
          {hideMark ? null : (
            <button
              type="button"
              className={clsx(heart, "right-2", liked ? "text-accent" : "text-white")}
              aria-pressed={liked}
              title={markHint ?? STAGE_HINT[stage]}
              aria-label={STAGE_LABEL[stage]}
              disabled={busy}
              onClick={() => onPick(shot.variantId, "picked")}
            >
              <StageIcon size={20} className={liked ? "fill-current" : undefined} />
            </button>
          )}
        </div>
        {arrows ? (
          <Arrow dir="next" disabled={arrows.index >= arrows.count - 1} onClick={() => arrows.onIndex(arrows.index + 1)} />
        ) : null}
      </div>
      <figcaption className="text-center text-[12.5px] text-white/80">
        {shot.label}
        {arrows && arrows.count > 1 ? <span className="text-white/50"> {arrows.index + 1} / {arrows.count}</span> : null}
      </figcaption>
    </figure>
  );
}

function InputsColumn({ title, inputs, index, onIndex }: { title: string; inputs: GenInput[]; index: number; onIndex: (i: number) => void }) {
  const cur = inputs[index];
  if (!cur) return null;
  return (
    <div className="grid min-w-0 content-start justify-items-center gap-2" data-annot={title}>
      <img
        key={cur.asset_id}
        src={mediaUrl(cur.path)}
        alt=""
        title={cur.hint ?? undefined}
        className="h-[70vh] w-full rounded-[4px] object-contain lg:h-[calc(100vh-10rem)]"
      />
      <div className="text-center text-[12.5px] text-white/80" title={cur.hint ?? undefined}>
        {cur.label || cur.role}
        {inputs.length > 1 ? <span className="text-white/50"> {index + 1} / {inputs.length}</span> : null}
      </div>
      {inputs.length > 1 ? (
        <div className="flex max-w-full flex-wrap justify-center gap-1.5" data-annot="лента входов">
          {inputs.map((i, n) => (
            <button
              key={`${i.asset_id}-${i.role}-${n}`}
              type="button"
              onClick={() => onIndex(n)}
              aria-label={`Показать: ${i.label || i.role}`}
              title={i.hint ? `${i.label}: ${i.hint}` : i.label}
              className="rounded-[4px]"
            >
              <img
                src={thumbUrl(i.path)}
                alt=""
                className={clsx(
                  "h-16 w-auto rounded-[4px] border object-cover",
                  n === index ? "border-accent ring-1 ring-accent" : "border-white/20",
                )}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const GRID_COLS = { 1: "", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" } as const;

export function CompareTriptych({
  card,
  startInput,
  host,
  onClose,
}: {
  card: ResultCard;
  startInput?: GenInput | undefined;
  host?: CompareHost | undefined;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const parent = card.edit_of ?? card.enhance_of;
  const needsOrigin = !!parent;
  const origin = useQuery({
    queryKey: resultKeys.card(parent?.variant_id ?? 0),
    queryFn: () => loadResultCard(parent!.variant_id),
    enabled: needsOrigin,
  });
  const t = triptych(card, origin.data);
  const inputs = t.needsOriginCard ? (origin.data?.generation.inputs ?? []) : card.generation.inputs;

  const split = splitInputs(inputs);
  const start = startIndices(split, startInput);
  const [productAt, setProductAt] = useState<number | null>(null);
  const [refAt, setRefAt] = useState<number | null>(null);
  const [editAt, setEditAt] = useState<number | null>(null);
  const editIndex = Math.min(editAt ?? t.startEdit, Math.max(t.edits.length - 1, 0));
  const productIndex = Math.min(productAt ?? start.product, Math.max(split.product.length - 1, 0));
  const refIndex = Math.min(refAt ?? start.refs, Math.max(split.refs.length - 1, 0));
  const count = t.edits.length;
  const refCount = split.refs.length;
  const productCount = split.product.length;

  const pick = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "picked" | "rejected" }) => pickResult(id, status),
    onSuccess: () => client.invalidateQueries({ queryKey: RESULTS_PREFIX }),
  });
  const note = enhanceNote(pick.data?.enhance ?? null);
  const onPick = (id: number, status: "picked" | "rejected") => {
    if (!pick.isPending) pick.mutate({ id, status });
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "SELECT" || el.tagName === "TEXTAREA")) return;
      if (count > 0) {
        const next = stepIndex(editIndex, count, event.key);
        if (next !== null) setEditAt(next);
      } else if (refCount > 0) {
        // без доработок ← → листают референс, без него — товар
        const next = stepIndex(refIndex, refCount, event.key);
        if (next !== null) setRefAt(next);
      } else {
        const next = stepIndex(productIndex, productCount, event.key);
        if (next !== null) setProductAt(next);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editIndex, count, refIndex, refCount, productIndex, productCount, onClose]);

  const originPending = t.needsOriginCard && !origin.data; // входы исходника ещё не пришли (или упали) — одна заглушка
  const inputCols = originPending ? 1 : (productCount > 0 ? 1 : 0) + (refCount > 0 ? 1 : 0);
  const cols = (inputCols + 1 + (count > 0 ? 1 : 0)) as 1 | 2 | 3 | 4;

  return (
    <div
      className="fixed inset-0 z-50 overflow-auto bg-black/75 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Сравнение"
      onClick={onClose}
      data-annot="сравнение"
    >
      <button
        type="button"
        className="absolute top-3 right-3 z-10 grid size-9 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
        autoFocus
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        title="Закрыть (Esc)"
        aria-label="Закрыть"
      >
        <X size={18} />
      </button>
      {pick.error || host?.error ? (
        <div className="absolute top-3 right-14 left-3 z-10 max-w-[600px]" onClick={(e) => e.stopPropagation()}>
          <ErrorLine error={pick.error ?? host?.error ?? null} />
        </div>
      ) : null}
      {note ? (
        <div className="relative z-10 mx-auto mt-8 max-w-[1600px] text-center text-[12.5px] text-white/80" onClick={(e) => e.stopPropagation()}>
          {note}
        </div>
      ) : null}
      <div
        className={clsx("mx-auto grid gap-4", cols === 4 ? "max-w-[2000px]" : "max-w-[1600px]", note ? "pt-2" : "pt-8", GRID_COLS[cols])}
        onClick={(e) => e.stopPropagation()}
      >
        {originPending ? (
          origin.isError ? (
            <div className="grid content-start gap-2" data-annot="входы">
              <ErrorLine error={origin.error} />
            </div>
          ) : (
            <div className="h-[70vh] w-full animate-pulse rounded-[4px] bg-white/10 lg:h-[calc(100vh-10rem)]" data-annot="входы" />
          )
        ) : (
          <>
            <InputsColumn title="товар" inputs={split.product} index={productIndex} onIndex={setProductAt} />
            <InputsColumn title="референс" inputs={split.refs} index={refIndex} onIndex={setRefAt} />
          </>
        )}
        <ShotColumn
          title="Результат"
          shot={t.generation}
          busy={host ? host.busy : pick.isPending}
          onPick={host ? (_id, status) => host.onPick(status) : onPick}
          hideMark={(t.needsOriginCard && !origin.data) || (!!host && !host.canLike)}
          markHint={host?.likeHint}
          newTab={!!host}
        />
        {count > 0 ? (
          <ShotColumn
            title="Доработка"
            shot={t.edits[editIndex]!}
            busy={pick.isPending}
            onPick={onPick}
            // в окне каталога лайка на доработке нет: picked на доработке/улучшении убирает платье из кандидатов каталога,
            // пара застрянет на «В работе», а доработка ещё и сразу платит за улучшение без рамки (№459)
            hideMark={!!host}
            arrows={{ index: editIndex, count, onIndex: setEditAt }}
            newTab={!!host}
          />
        ) : null}
      </div>
    </div>
  );
}
